import { createElement, lazy, type ComponentProps, type ComponentType } from 'react'

// `any` here is the canonical React idiom for "any component" — `unknown` breaks the ComponentType constraint.
// oxlint-disable-next-line typescript-eslint/no-explicit-any
type Loader<T extends ComponentType<any>> = () => Promise<{ default: T }>

// oxlint-disable-next-line typescript-eslint/no-explicit-any
export type PreloadableComponent<T extends ComponentType<any>> = ComponentType<ComponentProps<T>> & {
  preload: () => Promise<{ default: T }>
}

// Like React.lazy, but adds a `.preload()` method and renders synchronously
// after the loader has resolved. Once `preload()` (or a suspended first
// render) has completed, subsequent mounts skip the Suspense boundary entirely
// — no fallback flash, no extra microtask hop.
//
// Uses createElement (not JSX) for the dynamic component reference: the React
// Compiler memoization pass miscompiles `<Resolved {...} />` here because the
// variable is captured from a mutable module-level slot, and it ends up
// emitting the binding name as a string host-element type.
// oxlint-disable-next-line typescript-eslint/no-explicit-any
export function lazyPreloadable<T extends ComponentType<any>>(loader: Loader<T>): PreloadableComponent<T> {
  let resolved: T | null = null
  let cached: Promise<{ default: T }> | null = null

  const sharedLoader = () => {
    if (cached) return cached
    cached = loader().then((mod) => {
      resolved = mod.default
      return mod
    })
    return cached
  }

  // React.lazy uses the same shared loader, so a `.preload()` call and a
  // suspended render share one Promise (and therefore one network/parse pass).
  const Lazy = lazy(sharedLoader)

  function Preloadable(props: ComponentProps<T>) {
    if (resolved) return createElement(resolved, props)
    return createElement(Lazy, props)
  }

  ;(Preloadable as PreloadableComponent<T>).preload = sharedLoader
  return Preloadable as PreloadableComponent<T>
}
